/**
 * Telegram Mini App integration.
 *
 * The storefront is DUAL-MODE by requirement (CLAUDE.md §2): it must work
 * identically as a plain web page opened from a Facebook or TikTok link, and
 * as a Mini App inside Telegram. So everything here degrades to `null` rather
 * than throwing, and no feature is gated on Telegram being present.
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name: string; last_name?: string; username?: string; language_code?: string };
    start_param?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  openTelegramLink(url: string): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  setHeaderColor(color: string): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  };
  MainButton: {
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return typeof window !== 'undefined' ? (window.Telegram?.WebApp ?? null) : null;
}

export const isMiniApp = (): boolean => {
  const app = getWebApp();
  // `initData` is empty when the page is merely loaded in a browser that
  // happens to have the script tag.
  return Boolean(app && app.initData.length > 0);
};

export function initTelegram(headerColor?: string): void {
  const app = getWebApp();
  if (!app) return;
  app.ready();
  app.expand();
  if (headerColor) {
    try {
      app.setHeaderColor(headerColor);
    } catch {
      // Older clients reject non-standard colours; the shop theme still wins
      // inside the page itself (§5: shop theme beats Telegram themeParams).
    }
  }
}

export function telegramUser() {
  return getWebApp()?.initDataUnsafe.user ?? null;
}

export function telegramInitData(): string | null {
  const app = getWebApp();
  return app && app.initData.length > 0 ? app.initData : null;
}

/** `?startapp=` payload — how a deep link carries an order code or a ref. */
export function startParam(): string | null {
  return getWebApp()?.initDataUnsafe.start_param ?? null;
}

export function haptic(kind: 'light' | 'success' | 'error'): void {
  const haptics = getWebApp()?.HapticFeedback;
  if (!haptics) return;
  if (kind === 'light') haptics.impactOccurred('light');
  else haptics.notificationOccurred(kind);
}

/**
 * Opening a t.me link from inside a Mini App needs the Telegram bridge; from
 * plain web it is an ordinary navigation.
 */
export function openTelegramLink(url: string): void {
  const app = getWebApp();
  if (app) app.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

/**
 * Telegram's own bottom MainButton. Using it for the primary action makes the
 * Mini App feel native instead of like a website in a webview; on plain web
 * the caller renders its own sticky button instead.
 */
export function useMainButtonAvailable(): boolean {
  return isMiniApp();
}

export function setMainButton(options: {
  text: string;
  onClick: () => void;
  enabled?: boolean;
  loading?: boolean;
}): () => void {
  const app = getWebApp();
  if (!app) return () => undefined;

  const button = app.MainButton;
  button.setText(options.text);
  if (options.enabled === false) button.disable();
  else button.enable();
  if (options.loading) button.showProgress(true);
  else button.hideProgress();
  button.onClick(options.onClick);
  button.show();

  return () => {
    button.offClick(options.onClick);
    button.hide();
  };
}
